/**
 * @file 計画ブロック (カレンダー由来) の集計 — 「時間のつかいかた」ビューの計算部。
 *
 * @what: 取り込み窓の合計を週あたりに均し、種類 (習い事 / おうちの学習 / …) と
 *   まなびの領域ごとの分に割り付ける。対応表に無い件名は「対応表にない予定」として残す
 *   (黙って落とさない)。複数領域に効く予定は分を等分する (目安、厳密な按分はしない)。
 * @why: カレンダーは計画の正本で、ここは読み替えだけ。週あたりに均すのは、窓の長さ
 *   (28 日既定) に表示が依存しないようにするため。
 * @failure: 窓の日付が壊れている場合は 1 日として扱う (ゼロ除算回避。sync 側 Validate が
 *   正なのでここでは防御のみ)。
 */

import type { StudySchedule } from "../api/schedule";
import type { AttainmentModel, Domain } from "./types";
import { unitsInCell } from "./index";
import { ruleForLabel, type ScheduleKind, type ScheduleMap } from "./scheduleMap";

export interface ScheduleAggregate {
  /** 取り込み窓の日数。 */
  windowDays: number;
  /** 週あたりの合計 (分)。 */
  weeklyTotalMinutes: number;
  /** 種類ごとの週あたり分 (0 の種類は含めない)。map の kinds 順。 */
  byKind: { kind: ScheduleKind; minutes: number }[];
  /** 領域ごとの週あたり分 (多い順)。 */
  byDomain: { domainId: string; minutes: number }[];
  /** 対応表に一致しなかった件名と週あたり分 (多い順)。 */
  unmapped: { label: string; minutes: number }[];
  /** 計画に 1 分でも現れた領域。 */
  plannedDomainIds: Set<string>;
}

function daysBetween(startYmd: string, endYmd: string): number {
  const start = Date.parse(`${startYmd}T00:00:00Z`);
  const end = Date.parse(`${endYmd}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return 1;
  return Math.max(1, Math.round((end - start) / 86_400_000));
}

export function aggregateSchedule(schedule: StudySchedule, map: ScheduleMap): ScheduleAggregate {
  const windowDays = daysBetween(schedule.window_start, schedule.window_end);
  const weekly = (rawMinutes: number) => Math.round((rawMinutes * 7) / windowDays);

  let totalRaw = 0;
  const kindRaw = new Map<string, number>();
  const domainRaw = new Map<string, number>();
  const unmappedRaw = new Map<string, number>();
  for (const block of schedule.blocks) {
    totalRaw += block.minutes;
    const rule = ruleForLabel(map, block.label);
    if (rule === null) {
      unmappedRaw.set(block.label, (unmappedRaw.get(block.label) ?? 0) + block.minutes);
      continue;
    }
    kindRaw.set(rule.kind, (kindRaw.get(rule.kind) ?? 0) + block.minutes);
    if (rule.domains.length > 0) {
      const share = block.minutes / rule.domains.length;
      for (const d of rule.domains) {
        domainRaw.set(d, (domainRaw.get(d) ?? 0) + share);
      }
    }
  }

  const byKind = map.kinds
    .map((kind) => ({ kind, minutes: weekly(kindRaw.get(kind.id) ?? 0) }))
    .filter((k) => k.minutes > 0);
  const byDomain = [...domainRaw.entries()]
    .map(([domainId, raw]) => ({ domainId, minutes: weekly(raw) }))
    .filter((d) => d.minutes > 0)
    .sort((a, b) => b.minutes - a.minutes);
  const unmapped = [...unmappedRaw.entries()]
    .map(([label, raw]) => ({ label, minutes: weekly(raw) }))
    .sort((a, b) => b.minutes - a.minutes);

  return {
    windowDays,
    weeklyTotalMinutes: weekly(totalRaw),
    byKind,
    byDomain,
    unmapped,
    plannedDomainIds: new Set(domainRaw.keys()),
  };
}

/**
 * いまのステップに (触れる水準を除く) 単元があるのに、計画に 1 分も現れていない領域。
 * 「足りない」と断じる材料ではなく、計画をながめるときの目のやり場として出す。
 */
export function uncoveredStageDomains(
  model: AttainmentModel,
  stageId: string,
  plannedDomainIds: ReadonlySet<string>,
): Domain[] {
  return model.domains.filter((d) => {
    if (plannedDomainIds.has(d.id)) return false;
    return unitsInCell(model, stageId, d.id).some((u) => u.expected[stageId] !== "experience");
  });
}
